/** Dependency-free SVG charts for the reporting pages. */

export interface Series {
  key: string;
  label: string;
  color: string;
}

interface StackedBarChartProps<T> {
  data: T[];
  series: Series[];
  xKey: keyof T & string;
  formatX?: (value: string) => string;
  /** Format the label shown above each column (defaults to the stacked total). */
  formatTotal?: (total: number) => string;
  height?: number;
  onBarClick?: (row: T) => void;
}

/** Vertical stacked bars with a total label above each column. */
export function StackedBarChart<T>({
  data,
  series,
  xKey,
  formatX,
  formatTotal,
  height = 320,
  onBarClick,
}: StackedBarChartProps<T>) {
  const val = (row: T, key: string): number => Number((row as Record<string, unknown>)[key] ?? 0);
  const totals = data.map((row) => series.reduce((sum, s) => sum + val(row, s.key), 0));
  const max = Math.max(1, ...totals);
  const plotH = height - 48; // room for x labels + total labels

  return (
    <div className="chart">
      <div className="chart-bars" style={{ height }}>
        {data.map((row, i) => {
          const total = totals[i];
          return (
            <div
              key={String(row[xKey]) + i}
              className={`chart-col${onBarClick ? ' clickable' : ''}`}
              onClick={onBarClick ? () => onBarClick(row) : undefined}
            >
              <span className="chart-total">
                {formatTotal ? formatTotal(total) : total.toLocaleString('en-AU')}
              </span>
              <div className="chart-stack" style={{ height: plotH }}>
                {series.map((s) => {
                  const v = val(row, s.key);
                  if (!v) return null;
                  return (
                    <div
                      key={s.key}
                      className="chart-seg"
                      style={{ height: `${(v / max) * plotH}px`, background: s.color }}
                      title={`${s.label}: ${v.toLocaleString('en-AU')}`}
                    />
                  );
                })}
              </div>
              <span className="chart-x">{formatX ? formatX(String(row[xKey])) : String(row[xKey])}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ChartLegend({ series }: { series: Series[] }) {
  return (
    <div className="legend">
      {series.map((s) => (
        <span key={s.key} className="legend-item">
          <i style={{ background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

/** A thin horizontal proportion bar (used in driver / reason breakdowns). */
export function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="minibar" aria-hidden>
      <span style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}
